import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { UserDb } from './user-db.service';
import { FoodDbService } from './food-db.service';
import { TelemetryService } from './telemetry.service';
import { DatabaseWrapper } from './database-wrapper';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordState } from '@cf-shared/data-model/user-log';
import { formatScheduledMealName } from 'util/formatting';

@Injectable()
export class MealActionsService {
  constructor(
    private alertCtrl: AlertController,
    private userDb: UserDb,
    private foodDb: FoodDbService,
    private telemetry: TelemetryService
  ) {}

  /**
   * Show confirmation alert and clear meal contents if confirmed.
   * Clears ingredients, basedOnFoodId, and autoPlanned flag.
   * Updates state from Planned to Scheduled if applicable.
   * @returns true if meal was cleared, false if cancelled
   */
  async clearMealAsync(meal: Log_Meal): Promise<boolean> {
    // show confirmation alert using existing translations
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.editMeal.alerts.clearMealTitle({ mealName: formatScheduledMealName(meal.name) }),
      message: LL.editMeal.alerts.clearMealMessage(),
      enableBackdropDismiss: true,
      buttons: [
        {
          text: LL.editMeal.buttons.clear(),
          role: AlertButtonRole.Primary,
          result: true,
        },
        {
          text: LL.common.buttons.cancel(),
          role: AlertButtonRole.Cancel,
        },
      ],
    });

    if (!confirmed) {
      return false;
    }

    // clear meal contents
    meal.basedOnFoodId = undefined;
    meal.ingredients = [];
    meal.autoPlanned = false;

    // recalculate nutrients (will be zeros)
    await this.foodDb.recalculateMealNutrientsAsync(meal);

    // update state: Planned -> Scheduled (if not Done/Skipped)
    if (meal.state !== LogRecordState.Skipped && meal.state !== LogRecordState.Done) {
      if (meal.state === LogRecordState.Planned) {
        meal.state = LogRecordState.Scheduled;
      }
    }

    // save to database
    const transaction = DatabaseWrapper.getTransactionId();
    await this.userDb.saveEntryAsync(transaction, meal);
    await this.userDb.updateDayValuesOnDiskAsync(transaction, getEntryLocalTimeInDesiredTimeZone(meal, this.userDb.debug.getNowLocal()));

    this.telemetry.trackEvent('action_meal_clear', {});
    return true;
  }
}
